import React from "react";
import { Link } from "react-router-dom";
import { ReactComponent as BrandMark } from "../../../assets/svg/Dsahboard/brand-stepup-icon.svg";
import { ReactComponent as PlansMark } from "../../../assets/svg/Dsahboard/plans-icon.svg";
import { ReactComponent as AddClientMark } from "../../../assets/svg/Dsahboard/add-client-icon.svg";
import brandRipple from "../../../assets/svg/Dsahboard/brand-ripple.json";
import plansRipple from "../../../assets/svg/Dsahboard/plans-ripple.json";
import clientRipple from "../../../assets/svg/Dsahboard/client-ripple.json";
import StepRipple from "./StepRipple";
import type { SetupStep, SetupStepId } from "../setupSteps";

/**
 * The disc at the head of a step, the halo behind it, and the colour its note
 * is written in.
 *
 * Each step keeps its own mark and its own colour whatever state it is in — a
 * completed step reads "Completed" in the colour it has had all along rather
 * than swapping to a tick, because the row is a place in a journey and not a
 * checkbox. The word is what says which state it is in, so the colour is never
 * the only thing carrying it.
 *
 * Nothing is greyed back, including a step waiting its turn. It has no link and
 * no "+ …" offer — see `PENDING_NOTE` in `setupSteps` — and that is enough to
 * say it is not ready without dimming a row the reader is meant to be looking
 * forward to.
 *
 * The discs are part of the artwork rather than a tint under a glyph: each file
 * carries its own circle and its own stroke, at 48px. The `ripple` beside it is
 * the loop that plays under the one step the reader is on — see `liveStep`.
 */
const STEP_STYLES: Record<
  SetupStepId,
  { Mark: React.ElementType; ripple: unknown; note: string }
> = {
  brand: { Mark: BrandMark, ripple: brandRipple, note: "#43AF87" },
  plans: { Mark: PlansMark, ripple: plansRipple, note: "#7337B0" },
  client: { Mark: AddClientMark, ripple: clientRipple, note: "#00BACB" },
};

/** The leg between two steps, travelled and not. */
const LEG_DONE = "#00899B";
const LEG_TODO = "#E9EDF1";

/**
 * Where a leg is drawn on a phone, in px.
 *
 * There the three steps are thirds of the card and the mark sits over its
 * words, so a leg cannot be a flex sibling taking up the slack — there is no
 * slack, and anything in the row would push the marks off their own centres.
 * It is laid over the row instead, which needs the two measurements the flex
 * layout never has to name:
 *
 *  - `LEG_TOP` is the disc's own middle: the row's 8px of padding, then half
 *    of a 48px mark. A leg that split the difference of the whole cell would
 *    sit over the words instead, and lower under a title that wrapped.
 *  - `LEG_INSET` is how far short of a disc's centre it stops — the disc's
 *    radius, and a little air so the rule meets the artwork rather than
 *    touching it.
 */
const LEG_TOP = 32;
const LEG_INSET = 28;

/**
 * Which step gets the halo: the one that is theirs to do, and only that one.
 *
 * That is the step with somewhere to go — a completed one is behind them and
 * one waiting its turn cannot be pressed, so neither has anything to draw the
 * eye to, and three loops pulsing at once would point at nothing. A list with
 * nothing left on it gets no halo at all.
 */
const liveStep = (steps: SetupStep[]) =>
  steps.findIndex((step) => step.state === "todo" && step.to);

interface SetupChecklistProps {
  steps: SetupStep[];
}

/**
 * The padding a step sits in, so the one that is a link has somewhere to
 * paint its hover without standing lower than the two beside it. Every row
 * carries it, and the list pulls the outermost 8px back out.
 *
 * Two shapes. On a phone the three stand side by side in thirds of the card,
 * so the mark goes over the words and both are centred under it — a column
 * that narrow has no room for a disc and a line of text on one line. From `sm`
 * it is a row again, the mark beside its words, only as wide as they are: what
 * is left over there belongs to the legs, which is what makes them stretch.
 */
const ROW =
  "w-full p-2 rounded-md flex flex-col items-center gap-2 text-center " +
  "sm:w-auto sm:min-w-0 sm:flex-row sm:gap-3 sm:text-left";

/**
 * What the welcome card shows a workspace that is still being set up: the three
 * things standing between it and a dashboard with figures on it.
 *
 * A step with somewhere to go is a link, and one without is not — pressing a
 * completed step would send the reader back to work they have finished, and one
 * still waiting its turn has nowhere to send them yet. Both are drawn the same
 * as the live step; only the note and the hover tell them apart.
 *
 * From `sm` the three sit on one line joined by legs; below it they stack and
 * the legs go, because a horizontal rule between stacked rows joins nothing.
 */
const SetupChecklist: React.FC<SetupChecklistProps> = ({ steps }) => {
  const live = liveStep(steps);

  return (
    <ul className="mt-3 sm:mt-4 -mx-2 grid grid-cols-3 items-start sm:flex sm:flex-row sm:items-center">
      {steps.map((step, index) => {
        const style = STEP_STYLES[step.id];
        const Mark = style.Mark;
        const to = step.to;

        // Travelled once the step before it is done — and the first leg
        // always, since the reader is standing at step one the moment the card
        // opens. Worked out once for both of the legs drawn below, which are
        // the same leg at two widths.
        const legColor =
          index === 1 || steps[index - 1]?.state === "done"
            ? LEG_DONE
            : LEG_TODO;

        const body = (
          <>
            {/* The mark, with the halo behind it on the live step. Both sit in
                one box so the ripple can be centred on the disc and run wider
                than it without pushing the words along. */}
            <span className="relative flex-shrink-0 w-12 h-12">
              {index === live && <StepRipple data={style.ripple} />}
              <Mark aria-hidden className="relative w-12 h-12" />
            </span>
            {/* Wrapping rather than truncating on a phone: a third of a card is
                not always wide enough for "Setup your brand" on one line, and a
                step clipped to "Setup your…" says less than a step on two
                lines. From `sm` the row is as wide as its words, so the
                truncation is only ever the card running out of room. */}
            <span className="min-w-0">
              <span className="block text-[12px] sm:text-[14px] font-medium leading-tight text-ink sm:truncate">
                {step.title}
              </span>
              <span
                className="block text-[12px] font-medium leading-tight sm:truncate"
                style={{ color: style.note }}
              >
                {step.note}
              </span>
            </span>
          </>
        );

        return (
          // The row carrying a leg takes an equal share of what is left over,
          // so the two legs grow and shrink together as the card does.
          <li
            key={step.id}
            // `relative` is what the phone's leg is positioned against — see
            // `LEG_TOP`. It changes nothing about the flex row from `sm` up.
            className={`relative flex items-center ${
              index === 0 ? "sm:flex-shrink-0" : "sm:flex-1 sm:min-w-0"
            }`}
          >
            {index > 0 && (
              <>
                {/* The leg into this step on a phone: laid across the gap
                    between this disc and the one before it, at their height —
                    see `LEG_TOP`. The left edge is measured from the step
                    before's centre, which is half this cell's width to the
                    left of it, and the three cells are equal thirds. */}
                <span
                  aria-hidden
                  className="absolute h-px sm:hidden"
                  style={{
                    top: LEG_TOP,
                    left: `calc(-50% + ${LEG_INSET}px)`,
                    right: `calc(50% + ${LEG_INSET}px)`,
                    backgroundColor: legColor,
                  }}
                />
                {/* The same leg from `sm` up, where it is a sibling of the two
                    steps it joins and takes the width they leave over. */}
                <span
                  aria-hidden
                  className="hidden sm:block h-px flex-1 min-w-[24px] mx-3"
                  style={{ backgroundColor: legColor }}
                />
              </>
            )}
            {to ? (
              // The whole row is the target, so what is pressed is the step
              // rather than the few words inside it — the "+ Add now" under
              // the title is where the eye lands, not a second control.
              //
              // A link rather than a button that navigates: it goes to a
              // screen of the app, so it behaves like every other way of
              // getting there — a middle click opens it in a tab, the status
              // bar says where it leads, and the row still reads as one step.
              <Link
                to={to}
                className={`${ROW} transition-colors hover:bg-[#F7FBFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40`}
              >
                {body}
              </Link>
            ) : (
              <div className={ROW}>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default SetupChecklist;
